
import { Component, OnInit} from '@angular/core';
import { MatDialog} from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { AuthService } from 'app/auth/auth.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { IPayment } from '../../models/payment';
import { PaymentService } from '../../service/payment.service';
import { PayDialog } from '../pay/pay-component';
import { UserInformationService } from 'app/services/user-info/user-info.service';
import { FirmApplicationComponent } from 'app/components/firms/firm-application/firm-application.component';
import { IndividualApplicationComponent } from 'app/components/individuals/individual-application/individual-application.component';
import { AtpApplicationComponent } from 'app/components/atp/atp-application/atp-application.component';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.scss']
})

export class PaymentsComponent implements OnInit {

  public processing: boolean = false;
  public paymentsSource = new MatTableDataSource<IPayment>();
  public payments: IPayment [] = [  ];
  public paymentColumns: string[] = ['type','name','appStatus','status','paymentCleared','amount','paymentMethod', 'comments', 'actions'];
  public cardColor: string = this.sharedService.blue + '20';


  constructor(public paymentService: PaymentService,
              public sharedService: SharedService,
              public authService: AuthService,
              public userService: UserInformationService,
              private toastr: ToastrService,
              private dialog: MatDialog,
              private router: Router) { }

  ngOnInit(): void {
    this.getUserPayments();
  }

  getUserPayments() {
    this.processing = true;
    this.userService.getUserLicenses().subscribe(result => {
      result.forEach(r => {
        r.payments.forEach(p => {
          let payment = p;
          payment.id = +r.id;
          payment.applicationId = +r.id;
          payment.licenseType = r.type;
          payment.entityName = r.name;
          payment.applicationStatus = r.status;
          this.payments.push(payment);
        });
      } );
      this.paymentsSource = new MatTableDataSource<IPayment>(this.payments);
      this.processing = false;
    }, error => {
      this.processing = false;
      this.toastr.error('Unable get user payments');
    });
  }

  canPay(payment: IPayment): boolean {
    return !payment.paid && 
           payment.applicationStatus != this.paymentService.paymentPendingStatus && 
           payment.applicationStatus != this.sharedService.pendingApplicationStatus && 
           payment.status != this.paymentService.paymentAwaitingClearanceStatus;
  }

  pay(entityType: string, feeType: string, id: number, comments: string, amount: string) {
    let showConfirmation = false;
    if (comments != null && comments.indexOf(this.paymentService.PaymentIssuedCommentSearchText) >= 0) {
      showConfirmation = true;
    }
    if (showConfirmation) {
      this.sharedService.openConfirm("Looks like you have already attempted a payment for this license.  For payments to completely clear through the system, it can take upto 3 business days. Most payments are processed within 24 hrs.  Do you want to continue?");
      this.sharedService.confirmed().subscribe(confirmed => {
        if (confirmed) {
          this.dialog.open(PayDialog, {
            data: {
              entityType: entityType,
              feeType: feeType,
              canPay: true,
              id: id,
              amount: amount
            },
            role: 'dialog',
            ariaLabel: "Fee Information Dialog",
            minWidth: !this.sharedService.mobile ? '400px' : '300px',
            maxWidth: !this.sharedService.mobile ? '800px' : '300px',
            maxHeight: !this.sharedService.mobile ? '800px' : '500px',
          });
        }
      });
    }
    else {
      this.dialog.open(PayDialog, {
        data: {
          entityType: entityType,
          feeType: feeType,
          canPay: true,
          id: id,
          amount: amount
        },
        role: 'dialog',
        ariaLabel: "Fee Information Dialog",
        minWidth: !this.sharedService.mobile ? '400px' : '300px',
        maxWidth: !this.sharedService.mobile ? '800px' : '300px',
        maxHeight: !this.sharedService.mobile ? '800px' : '500px',
      });
    }
  }

  public getPaymentStatusColor(status: string): string {
    if (status === 'Pending') {
      return this.sharedService.getStatusColor('In Progress');
    } else if (status === this.paymentService.paymentAwaitingClearanceStatus) {
      return this.sharedService.getStatusColor('Action Needed');
    } else if (status === this.paymentService.paymentClearedStatus) {
      return this.sharedService.getStatusColor('Approved');
    } else if (status === this.paymentService.paymentVoidedStatus) {
      return this.sharedService.getStatusColor('Cancelled');
    }
    return this.sharedService.getStatusColor('In Progress');
  }

  public openApplicationDetails(payment: IPayment): void {
    const type = payment.licenseType || payment.entityType;

    if (type === 'FirmApplication') {
      const applicationId = payment.firmApplicationId || payment.applicationId;
      if (!applicationId) {
        this.toastr.error('Unable to open the Firm application.');
        return;
      }
      this.dialog.open(FirmApplicationComponent, {
        width: '95%',
        autoFocus: false,
        data: { applicationId }
      });
      return;
    }

    if (type === 'PersonApplication') {
      const applicationId = payment.personApplicationId || payment.applicationId;
      if (!applicationId) {
        this.toastr.error('Unable to open the Individual application.');
        return;
      }
      this.dialog.open(IndividualApplicationComponent, {
        width: '95%',
        autoFocus: false,
        data: { applicationId }
      });
      return;
    }

    if (type === 'AtpApplication' || type === 'AtpAccreditation' || type === 'Atp') {
      const atpApplicationId = payment.atpApplicationId
        || (type === 'AtpApplication' ? payment.applicationId : undefined);
      if (!atpApplicationId) {
        this.toastr.error('Unable to open the ATP application.');
        return;
      }
      this.dialog.open(AtpApplicationComponent, this.sharedService.getWizardDialogConfig({
        data: {
          atpApplicationId,
          atpId: payment.atpId
        }
      }));
    }
  }

  public getApplicationTypeLabel(payment: IPayment): string {
    switch (payment.licenseType || payment.entityType) {
      case 'FirmApplication':
        return 'Firm Application';
      case 'PersonApplication':
        return 'Individual Application';
      case 'AtpApplication':
        return 'ATP Application';
      case 'AtpAccreditation':
        return 'ATP Accreditation';
      case 'AtpCourse':
        return 'ATP Course';
      default:
        return 'Application';
    }
  }

}


